import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger, getCorrelationId } from '../../lib/logger';
import { BrandConfig } from '../../app/config/brand';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  correlationId: string | undefined;
}

/**
 * React Error Boundary for catching and logging component errors
 *
 * Features:
 * - Catches JavaScript errors in child components
 * - Logs errors with correlation ID for tracing
 * - Displays a fallback UI instead of crashing
 * - Provides retry functionality
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      correlationId: undefined,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      correlationId: getCorrelationId(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const correlationId = getCorrelationId();

    // Log the error with structured context
    logger.error('React component error', {
      correlationId,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      correlationId: undefined,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              We're sorry, but something unexpected happened. Please try again.
            </p>
            {this.state.correlationId && (
              <p className="text-sm text-gray-400 mb-6">
                Reference: {this.state.correlationId}
              </p>
            )}
            <div className="space-x-4">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 rounded-md hover:opacity-90 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                style={{ backgroundColor: BrandConfig.colors.primary, color: BrandConfig.colors.onPrimary }}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
